import { jsxs, jsx } from "react/jsx-runtime";
import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { cn } from "../../lib/utils.js";
import "./shared/quiz-blank.js";
import { KatexText } from "./shared/katex-text.js";
import { useTranslation } from "react-i18next";
import "./shared/quiz-style-context.js";
import { SubmitButton } from "./shared/submit-button.js";
import "../../node_modules/.pnpm/hls.js@1.6.15/node_modules/hls.js/dist/hls.js";
import { QuizMedia } from "./shared/quiz-media.js";
import "../../context/DevToolsContext.js";
import { formatText } from "../../utils/quiz-parser.js";
import { motion } from "../../node_modules/.pnpm/framer-motion@11.18.2_react-dom@18.3.1_react@18.3.1__react@18.3.1/node_modules/framer-motion/dist/es/render/components/motion/proxy.js";
function MultiChoiceQuizForDiary({
  quizId,
  quiz,
  onSolve,
  solveResult,
  isViewSolved = false,
  initialAnswers: initialAnswersProp,
  submitLabel,
  hideSubmitButton = false
}) {
  const { t } = useTranslation("quiz");
  const [startTime] = useState(() => Date.now());
  const isSolved = solveResult !== null && solveResult !== void 0;
  const answerCount = quiz.answerCount || 1;
  const sortedAnswers = useMemo(() => {
    if (quiz.random && Array.isArray(quiz.random) && quiz.random.length > 0) {
      return quiz.random.map((idx) => quiz.choices[idx]).filter(Boolean);
    }
    return quiz.choices;
  }, [quiz.choices, quiz.random]);
  const computedInitialKeys = useMemo(() => {
    if (initialAnswersProp && initialAnswersProp.length > 0) {
      return initialAnswersProp;
    }
    return [];
  }, [initialAnswersProp]);
  const [selectedKeys, setSelectedKeys] = useState(computedInitialKeys);
  const isInitialMount = useRef(true);
  useEffect(() => {
    setSelectedKeys((prev) => {
      if (prev.length === computedInitialKeys.length && prev.every((v, i) => v === computedInitialKeys[i])) {
        return prev;
      }
      return computedInitialKeys;
    });
  }, [computedInitialKeys]);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (hideSubmitButton && !isSolved && !isViewSolved) {
      onSolve == null ? void 0 : onSolve(quizId, selectedKeys, quiz.vars, Date.now() - startTime);
    }
  }, [selectedKeys, hideSubmitButton, isSolved, isViewSolved, onSolve, quizId, quiz.vars, startTime]);
  const canSubmit = selectedKeys.length > 0;
  const handleSelect = useCallback((key) => {
    if (isSolved || isViewSolved) return;
    setSelectedKeys((prev) => {
      if (prev.includes(key)) {
        return prev.filter((k) => k !== key);
      }
      return [...prev, key];
    });
  }, [isSolved, isViewSolved]);
  const handleSubmit = useCallback(() => {
    if (!canSubmit || isSolved) return;
    const elapsedTime = Date.now() - startTime;
    onSolve == null ? void 0 : onSolve(quizId, selectedKeys, quiz.vars, elapsedTime);
  }, [selectedKeys, canSubmit, isSolved, startTime, onSolve, quizId, quiz.vars]);
  return /* @__PURE__ */ jsxs("div", { className: "space-y-6", children: [
    /* @__PURE__ */ jsxs(
      motion.div,
      {
        initial: { opacity: 0, y: -10 },
        animate: { opacity: 1, y: 0 },
        className: "space-y-3",
        children: [
          /* @__PURE__ */ jsxs("div", { className: "inline-flex gap-2 items-center px-3 py-1.5 text-sm font-bold text-white bg-linear-to-r from-violet-500 to-purple-500 rounded-full shadow-2xs", children: [
            /* @__PURE__ */ jsx("span", { children: t("types.newMultiChoice.label") }),
            answerCount > 1 && /* @__PURE__ */ jsx("span", { className: "px-1.5 py-0.5 bg-white/20 rounded-xs text-[10px]", children: t("types.newMultiChoice.selectCount", { count: answerCount }) })
          ] }),
          /* @__PURE__ */ jsx("h3", { className: "text-xl font-bold leading-relaxed text-gray-900", children: /* @__PURE__ */ jsx(KatexText, { text: quiz.title }) }),
          quiz.problem && /* @__PURE__ */ jsx("div", { className: "text-lg leading-relaxed text-gray-700", children: formatText(quiz.problem, `${quizId}-problem`, quiz.additionalImages ?? void 0) }),
          /* @__PURE__ */ jsx(QuizMedia, { image: quiz.image, imageMobile: quiz.imageMobile, desmos: quiz.desmos, vars: quiz.vars })
        ]
      }
    ),
    !isSolved && !isViewSolved && answerCount > 1 && /* @__PURE__ */ jsx("p", { className: "text-base text-gray-500", children: t("types.newMultiChoice.selectedProgress", { selected: selectedKeys.length, count: answerCount }) }),
    /* @__PURE__ */ jsx(
      "div",
      {
        role: "listbox",
        "aria-label": t("types.multiChoice.selectOption"),
        "aria-multiselectable": "true",
        className: "flex flex-wrap gap-2",
        children: sortedAnswers.map((answer, idx) => {
          const isSelected = selectedKeys.includes(answer.key);
          return /* @__PURE__ */ jsxs(
            motion.button,
            {
              role: "option",
              "aria-selected": isSelected,
              initial: { opacity: 0, scale: 0.95 },
              animate: { opacity: 1, scale: 1 },
              transition: { delay: idx * 0.03 },
              onClick: () => handleSelect(answer.key),
              disabled: isSolved || isViewSolved,
              className: cn(
                "px-3 py-1.5 rounded-lg transition-all text-base font-medium",
                // 기본 상태
                !isSelected && "bg-gray-100 text-gray-600 hover:bg-violet-100 hover:text-violet-700",
                // 선택 상태
                isSelected && "bg-violet-500 text-white shadow-2xs",
                // 제출 후 비활성화
                (isSolved || isViewSolved) && "cursor-not-allowed opacity-70"
              ),
              children: [
                /* @__PURE__ */ jsx("span", { className: "font-bold", children: "#" }),
                /* @__PURE__ */ jsx(KatexText, { text: answer.answer })
              ]
            },
            answer.key
          );
        })
      }
    ),
    !isSolved && !isViewSolved && !hideSubmitButton && /* @__PURE__ */ jsx(
      SubmitButton,
      {
        onClick: handleSubmit,
        disabled: !canSubmit,
        label: submitLabel ?? (selectedKeys.length === 0 ? t("types.newMultiChoice.selectOption") : t("types.newMultiChoice.submit"))
      }
    ),
    isSolved && /* @__PURE__ */ jsx("div", { className: "p-4 text-center text-gray-600 bg-gray-100 rounded-xl", children: t("types.newMultiChoice.submitted") })
  ] });
}
export {
  MultiChoiceQuizForDiary
};
//# sourceMappingURL=new-multi-choice-quiz.js.map
